/** Subscription KEYWORD presets for Admin Task > School List.

School List can filter by ONE subscription (SubscriptionPicker -> subscription_id)
or by a subscription KEYWORD, which is the union of every subscription the keyword
matches. Keyword mode is resolved on the client — `subscription_get(keyword)` for
the ids, then one `get_schools_by_subscription` per id — so it needs no new
backend route.

A PRESET is a named keyword resolved against subscription NAMES only, rather than
handed to the server as-is. `subscription_get` runs one LIKE over (name, name_en,
**remarks**), and on this data remarks is not a safe thing to match:
'Eduventure Spring 2026' has nothing to do with the programme but copy-pasted its
URL (https://elafp.edb.edcity.hk/...) into its remarks, so a server-side keyword
match listed two schools — 香港中文大學 and pgde5022-2526 — that hold no eLAFP
grant at all. Hence `filterSubscriptionsForPreset`: ask the server, then keep only
the rows whose `name` / `name_en` carries the keyword.

WHAT THIS DELIBERATELY EXCLUDES. The 2024 QEF round — subscriptions 1 and 2,
'優質教育基金電子學習配套計劃(2024 小學/中學)' /
'QEF e-Learning Ancillary Facilities Programme (2024 Pri|Sec)' — is the same
programme spelled out (eLAFP *is* e-Learning Ancillary Facilities Programme) and
its remarks point at the official programme site. It is nonetheless OUT of this
filter, by decision on 2026-09-01: the button means the 26/27 rounds, and pulling
in the 2024 cohort roughly doubled the list (82 schools against 42). This was
briefly implemented the other way, with a second 'Ancillary Facilities' keyword
that matched exactly those two rows; if the 2024 round is ever wanted back, that
keyword is the whole change. It is a decision about scope, NOT a data quirk to be
re-fixed — a future reader finding 五旬節聖潔會永光小學 absent from an eLAFP list
is looking at this paragraph, not at a bug.

So the preset keeps a keyword LIST even though eLAFP currently needs one entry:
the second entry is the documented way back, and a programme whose rounds are
named inconsistently would need it again.

Everything here is a keyword, never a subscription id, so a future round
(eLAFP(27/28), …) joins the filter the moment its rows exist — see the note on
hardcoded ids in the School List docs.
*/

export const ELAFP_FILTER_KEYWORD = 'eLAFP';

export const ELAFP_FILTER_PRESET = {
  keyword: ELAFP_FILTER_KEYWORD,
  // One entry on purpose — see "WHAT THIS DELIBERATELY EXCLUDES" above.
  keywords: [ELAFP_FILTER_KEYWORD],
};

export const SUBSCRIPTION_KEYWORD_PRESETS = [ELAFP_FILTER_PRESET];

/** The preset a keyword names, or null for a plain (server-matched) keyword. */
export function subscriptionKeywordPreset(keyword) {
  const needle = (keyword || '').trim().toLowerCase();
  if (needle === '') return null;
  return SUBSCRIPTION_KEYWORD_PRESETS.find(
    (preset) => preset.keyword.toLowerCase() === needle,
  ) || null;
}

/** True when a subscription's NAME (either language) carries one of `keywords`. */
export function subscriptionNameMatchesKeywords(sub, keywords) {
  if (!sub || !Array.isArray(keywords)) return false;
  const haystack = `${sub.name || ''}\n${sub.name_en || ''}`.toLowerCase();
  return keywords.some((keyword) => {
    const needle = String(keyword || '').trim().toLowerCase();
    return needle !== '' && haystack.includes(needle);
  });
}

/**
 * Narrow one `subscription_get` page to the rows a preset actually claims.
 * A plain keyword keeps whatever the server matched, remarks included.
 */
export function filterSubscriptionsForPreset(rows, preset) {
  const list = Array.isArray(rows) ? rows : [];
  const valid = list.filter((sub) => sub && (parseInt(sub.id, 10) || 0) > 0);
  if (!preset) return valid;
  return valid.filter((sub) => subscriptionNameMatchesKeywords(sub, preset.keywords));
}
